import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { DASHBOARD_DEFAULT_BASE_NAME } from '@/entities/dashboard';
import type { UserConfig } from '@/entities/user-config';
import { userConfigQueryKey } from '@/features/_shared/user';
import type { DashboardTab } from '@/features/dashboard/types';
import { usePreferences } from '@/features/profile';
import useSnackbarNotifications from '@/shared/hooks/useSnackbarNotifications';

import { dashboardOptions } from '../dashboard.options';
import { isDashboardConsistencyTimeoutError } from '../dashboardReconciliation';
import { commitConfirmedDashboardConfig, deleteDashboardFromApi } from './dashboardMutation.api';
import {
  buildDashboardPath,
  buildOptimisticDeleteCache,
  createHomepageOption,
  getPostDeleteNavTarget,
  homepageMatchesDashboard
} from './dashboardMutation.utils';

const getCurrentHomepageValue = (homepageValue: string, cachedConfig?: UserConfig): string => {
  const cachedHomepage = cachedConfig?.homepage;
  return cachedHomepage?.value.length ? cachedHomepage.value : homepageValue;
};

export const useDeleteDashboard = (dashboardName: string) => {
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbarNotifications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { changeUserSetting, homepage } = usePreferences();

  const queryKey = dashboardOptions.all();

  return useMutation({
    mutationKey: dashboardOptions.mutations.delete(dashboardName),

    mutationFn: async () => {
      const retainedDashboards =
        queryClient.getQueryData<Record<string, DashboardTab>>(queryKey) ?? {};

      return {
        dashboards: await deleteDashboardFromApi(dashboardName, retainedDashboards)
      };
    },

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: dashboardOptions.all() });
      const previousCache = queryClient.getQueryData<Record<string, DashboardTab>>(queryKey);

      const updatedCache = previousCache
        ? buildOptimisticDeleteCache(previousCache, dashboardName)
        : {};

      if (previousCache) {
        queryClient.setQueryData(queryKey, updatedCache);
      }

      // Navigate immediately so the URL stays consistent with the updated cache.
      const navTarget = getPostDeleteNavTarget(updatedCache);
      if (navTarget) {
        navigate(buildDashboardPath(navTarget.dashboardName, navTarget.tabName));
      } else {
        navigate('/dashboard/new');
      }

      // Preserve enough info to undo the navigation if the API call fails.
      const firstDeletedTab = previousCache?.[dashboardName]
        ? Object.keys(previousCache[dashboardName])[0]
        : undefined;
      const rollbackPath = firstDeletedTab
        ? buildDashboardPath(dashboardName, firstDeletedTab)
        : '/dashboard/new';

      return { previousCache, navTarget, rollbackPath };
    },

    onError: (error, _variables, context) => {
      if (isDashboardConsistencyTimeoutError(error)) {
        showErrorSnackbar("Oops! We couldn't delete your dashboard. Please try again.");
        return;
      }
      if (context?.previousCache) {
        queryClient.setQueryData(queryKey, context.previousCache);
      }
      if (context) {
        navigate(context.rollbackPath);
      }
      showErrorSnackbar("Oops! We couldn't delete your dashboard. Please try again.");
    },

    onSuccess: ({ dashboards }) => {
      commitConfirmedDashboardConfig(queryClient, dashboards);
      showSuccessSnackbar('Dashboard deleted!');

      const homepageValue = getCurrentHomepageValue(
        homepage.value,
        queryClient.getQueryData<UserConfig>(userConfigQueryKey)
      );
      const wasHomepage = homepageMatchesDashboard(homepageValue, dashboardName);
      if (wasHomepage) {
        const updatedCache = queryClient.getQueryData<Record<string, DashboardTab>>(queryKey) ?? {};
        const navTarget = getPostDeleteNavTarget(updatedCache);
        changeUserSetting(
          'homepage',
          navTarget
            ? createHomepageOption(navTarget.dashboardName)
            : { label: DASHBOARD_DEFAULT_BASE_NAME, value: 'my-dashboard' },
          false
        );
      }
    }
  });
};
